import {put, fork, throttle, take, race, call, takeEvery} from "redux-saga/effects";

import * as CollectionActions from "@atg-casino-shared/data-access-collection";
import * as GameActions from "@atg-casino-shared/data-access-game";
import {RESET_ALL} from "@atg-casino-shared/types-root";
import {LimitsActions} from "@atg-responsible-gambling-shared/limits-domain";
import {SelfExclusionActions} from "@atg-responsible-gambling-shared/self-exclusion-domain";
import {SelfExclusionConstants} from "@atg-responsible-gambling-shared/self-exclusion-types";
import {ModalActions} from "@atg-shared/modals-data-access";
import {MODAL_SHOW, MODAL_CLOSE, CLOSE_ALL_MODALS} from "@atg-shared/modals-types";

import type {SagaIterator} from "redux-saga";
import type {Effect} from "redux-saga/effects";

const resetAll = () => ({
    type: RESET_ALL,
});

export function* shouldReset(): SagaIterator {
    yield put(resetAll());
}

export function* modalClosed(modalId: string): SagaIterator {
    let payload;
    let type;
    while (type !== CLOSE_ALL_MODALS && payload !== modalId) {
        ({type, payload} = yield take([MODAL_CLOSE, CLOSE_ALL_MODALS]));
    }
}

export function* setLossLimitModalSaga(): SagaIterator {
    yield put(LimitsActions.resetStatus());

    const {set} = yield race({
        set: take(LimitsActions.REQUEST_GET_RGS_LIMITS),
        closed: call(modalClosed, "casinoLossLimitModal"),
    });

    if (set) {
        yield take(LimitsActions.RECEIVE_GET_RGS_LIMITS);
        yield put(ModalActions.closeCasinoLossLimitModal());
    }
}

export function* panicModalSaga(): SagaIterator {
    yield put(SelfExclusionActions.clearSelfExclusionStatusMessages());

    const {set} = yield race({
        set: take(SelfExclusionConstants.REQUEST_PANIC_SELF_EXCLUSION),
        closed: call(modalClosed, "casinoPanicModal"),
    });

    if (set) {
        yield take(SelfExclusionConstants.RECEIVE_PANIC_SELF_EXCLUSION);
        yield put(ModalActions.closeCasinoPanicModal());
    }
}

/**
 * Keeps self-exclusion data in sync after a casino panic self-exclusion.
 *
 * @remarks
 * The panic endpoint responds with an empty body, so nothing in the store
 * reflects the new exclusion until it is re-fetched. Refreshing here is what
 * lets an ongoing play session notice that the user just excluded themselves.
 */
export function* refreshSelfExclusionsOnPanicSaga(): SagaIterator {
    yield takeEvery(
        SelfExclusionConstants.RECEIVE_PANIC_SELF_EXCLUSION,
        function* refreshSelfExclusions(): SagaIterator {
            yield put(SelfExclusionActions.getSelfExclusions());
        },
    );
}

export function* casinoLimitModalsSaga(): SagaIterator {
    while (true) {
        const {payload} = yield take(MODAL_SHOW);

        if (payload === "casinoPanicModal") {
            yield fork(panicModalSaga);
        } else if (payload === "casinoLossLimitModal") {
            yield fork(setLossLimitModalSaga);
        }
    }
}

export function* casinoSaga(): Generator<Effect, void, void> {
    yield throttle(
        10 * 60 * 1000,
        [
            GameActions.REQUEST_GAME,
            GameActions.REQUEST_GAMES,
            CollectionActions.REQUEST_COLLECTION,
        ],
        shouldReset,
    );

    yield fork(casinoLimitModalsSaga);
    yield fork(refreshSelfExclusionsOnPanicSaga);
}
